import { useMemo, useState } from "react";
import { latestSetForSku, mediaSlotLabel } from "../../api/amazonMedia";
import { StateBanner } from "../../components/StateBanner";
import { Badge } from "./AmzUi";
import { MediaThumb } from "./LibraryView";
import { useAmazonMedia } from "./useAmazonMedia";

interface LibraryPickerProps {
  sku: string;
  current: string[];
  max: number;
  onApply: (urls: string[], message: string) => void;
  onClose: () => void;
}

/**
 * 画像タブ「画像ライブラリから追加」。
 * ・このSKUの最新版一式（メイン→2枚目→…）で画像枠をまるごと置き換える
 * ・または選んだ画像を末尾に追加する（クリック順＝追加順・重複と上限超えは入れない）
 * 反映先は下書きだけ（Amazon への送信は「変更を確認」から）。
 */
export function LibraryPicker({ sku, current, max, onApply, onClose }: LibraryPickerProps) {
  const { data, error, reload } = useAmazonMedia();
  const [others, setOthers] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);

  const items = useMemo(
    () => (data?.items ?? []).filter((i) => i.latest && (others || i.sku === sku)).sort((a, b) => (a.sku === b.sku ? a.order - b.order : a.sku.localeCompare(b.sku))),
    [data, others, sku],
  );
  const set = useMemo(() => latestSetForSku(data?.items ?? [], sku), [data, sku]);
  const setUrls = set.map((i) => i.url).slice(0, max);
  const sameAsSet = setUrls.length > 0 && setUrls.length === current.length && setUrls.every((u, i) => u === current[i]);
  const room = Math.max(0, max - current.length);

  const toggle = (url: string) =>
    setPicked((p) => (p.includes(url) ? p.filter((u) => u !== url) : p.length >= room ? p : [...p, url]));

  return (
    <div className="amz-overlay" role="dialog" aria-modal="true" aria-label="画像ライブラリから追加">
      <div className="amz-modal">
        <div className="amz-modal-head">
          <h2>画像ライブラリから追加</h2>
          <label className="amz-lib-check">
            <input type="checkbox" checked={others} onChange={(e) => setOthers(e.target.checked)} />
            他のSKUの画像も表示
          </label>
        </div>
        <div className="amz-modal-body">
          {error && <StateBanner kind="error" message={error} onRetry={() => void reload()} />}
          {!data && !error && <StateBanner kind="loading" message="画像ライブラリを読み込んでいます。" />}
          {data && set.length > 0 && (
            <div className="amz-lib-setbar">
              <div>
                <b>{sku} の画像一式（{set.length}枚）</b>
                <span className="amz-hint">メイン→2枚目→… の順で、今の画像枠をすべて置き換えます。</span>
              </div>
              <button
                type="button"
                className="amz-btn primary sm"
                disabled={sameAsSet}
                onClick={() => onApply(setUrls, `${sku} の画像一式（${setUrls.length}枚）で置き換えました（下書き）。`)}
              >
                {sameAsSet ? "反映済み" : "一式で置き換える"}
              </button>
            </div>
          )}
          {data && set.length === 0 && !others && (
            <div className="amz-empty">このSKUの画像はライブラリにまだありません。「他のSKUの画像も表示」から選べます。</div>
          )}
          {data && items.length > 0 && (
            <>
              <span className="amz-sec-label">
                1枚ずつ選んで末尾に追加（クリックした順・あと{room}枚まで）
              </span>
              <div className="amz-lib-grid pick">
                {items.map((it) => {
                  const used = current.includes(it.url);
                  const n = picked.indexOf(it.url);
                  return (
                    <button
                      key={it.file}
                      type="button"
                      className={`amz-lib-card pickable ${n >= 0 ? "picked" : ""}`}
                      disabled={used}
                      onClick={() => toggle(it.url)}
                      title={it.file}
                    >
                      <MediaThumb url={it.url} />
                      <div className="amz-lib-meta">
                        <div className="amz-lib-row">
                          <Badge tone={it.slot === "main" ? "accent" : "neutral"} size="xs">
                            {mediaSlotLabel(it)}
                          </Badge>
                          {used && (
                            <Badge tone="success" size="xs">
                              使用中
                            </Badge>
                          )}
                          {n >= 0 && <span className="amz-lib-pickno">{n + 1}</span>}
                        </div>
                        {others && <span className="amz-lib-file">{it.sku}</span>}
                        {it.caption && <span className="amz-lib-cap">{it.caption}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="amz-modal-foot end">
          <div className="amz-modal-foot-btns">
            <button type="button" className="amz-btn" onClick={onClose}>
              閉じる
            </button>
            <button
              type="button"
              className="amz-btn primary"
              disabled={picked.length === 0}
              onClick={() => onApply([...current, ...picked].slice(0, max), `${picked.length}枚を末尾に追加しました（下書き）。`)}
            >
              選んだ画像を追加{picked.length > 0 ? `（${picked.length}枚）` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
